import React, { useState } from 'react';
import UISectionWrapper, {
  UISectionHeader,
  UISectionBody,
  UISectionFooter,
  UIFormRow,
  UIFormLabels,
  UIFormFields,
  UIFormLabel,
} from '@/components/shared/UISectionWrapper';
import UIButton from '@/components/shared/UIButton';
import UIInput from '@/components/shared/UIInput';
import UIAvatarUpload from '@/components/shared/UIAvatarUpload/UIAvatarUpload';
import UISpinner from '@/components/shared/UISpinner';
import type { AvatarFile } from '@/components/shared/UIAvatarUpload/UIAvatarUpload';
import { useProfile } from '@/hooks/useProfile';
import { useToast } from '@/components/shared/ToastProvider';
import './GeneralSettingsSection.scss';
import UISectionTitle from '@/components/shared/UISectionTitle';

const getGuaranteedAvatarUrl = (path?: string | null): string => {
  const localPlaceholder = '/ava/photo.png';

  if (!path) {
    return localPlaceholder;
  }

  if (path.startsWith('http') || path.startsWith('data:')) {
    return path;
  }

  if (path.startsWith('/ava/')) {
    return path;
  }

  const backendUrl = `http://${process.env.REACT_APP_BACKEND_HOST}:${process.env.REACT_APP_BACKEND_PORT}`;
  const pathWithoutLeadingSlash = path.startsWith('/')
    ? path.substring(1)
    : path;

  return `${backendUrl}/${pathWithoutLeadingSlash}`;
};

export interface GeneralSettingsSectionProps {
  className?: string;
}

const baseClass = 'GeneralSettingsSection';

const GeneralSettingsSection: React.FC<GeneralSettingsSectionProps> = ({
  className = '',
}) => {
  const [selectedAvatar, setSelectedAvatar] = useState<AvatarFile | null>(null);
  const [saving, setSaving] = useState(false);
  const [avatarToDelete, setAvatarToDelete] = useState(false);

  const { showError } = useToast();

  const { data, initialData, loading, handleChange, handleSave, handleClear } =
    useProfile('general');

  const handleAvatarSelect = (avatarFile: AvatarFile | null) => {
    if (avatarFile && avatarFile.uploadedUrl) {
      // Файл успішно завантажився, зберігаємо його
      setSelectedAvatar(avatarFile);
      setAvatarToDelete(false);
    } else if (avatarFile && !avatarFile.uploadedUrl) {
      // Файл ще завантажується, зберігаємо тимчасово
      setSelectedAvatar(avatarFile);
      setAvatarToDelete(false);
    } else {
      // Помилка або скасування
      setSelectedAvatar(null);
    }
  };

  const handleAvatarRemove = () => {
    setSelectedAvatar(null);
    setAvatarToDelete(true);
  };

  const handleAvatarError = (errorMessage: string) => {
    setSelectedAvatar(null);
    showError(errorMessage);
  };

  const handleSaveClick = async () => {
    try {
      setSaving(true);

      const extraData: { avatarFileId?: number | null } = {}; // змінити тип

      if (selectedAvatar?.uploadedFileId) {
        // Передаємо ID файла замість URL
        extraData.avatarFileId = selectedAvatar.uploadedFileId;
      } else if (avatarToDelete) {
        // Видаляємо аватар
        extraData.avatarFileId = null;
      }
      // Перевірка що файл завантажився
      else if (selectedAvatar && !selectedAvatar.uploadedFileId) {
        showError('Please wait for the avatar to finish uploading');
        return;
      }

      await handleSave(extraData);

      setSelectedAvatar(null);
      setAvatarToDelete(false);
    } catch (err) {
      console.error('Error saving profile:', err);
    } finally {
      setSaving(false);
    }
  };
  const handleClearClick = () => {
    handleClear();
    setSelectedAvatar(null);
    setAvatarToDelete(false);
  };

  const getCurrentAvatar = () => {
    if (selectedAvatar?.preview) {
      return selectedAvatar.preview;
    }
    if (avatarToDelete) {
      return undefined;
    }
    return getGuaranteedAvatarUrl(data.avatar);
  };

  const hasChanges =
    data.firstname !== initialData.firstname ||
    data.lastname !== initialData.lastname ||
    selectedAvatar !== null ||
    avatarToDelete;

  // Перевіряємо чи можна зберігати (аватар має бути завантажений)
  const canSave =
    hasChanges &&
    (!selectedAvatar || selectedAvatar.uploadedFileId) && // перевіряємо uploadedFileId
    !saving;

  if (loading) {
    return (
      <div
        className={`${baseClass}__loading`}
        style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}
      >
        <UISpinner />
      </div>
    );
  }

  return (
    <UISectionWrapper
      className={`${baseClass} ${className}`}
      layout="form-horizontal"
      formPadding="large"
      labelWidth="127px"
      formGap="15px"
      align="stretch"
    >
      <UISectionHeader>
        <UISectionTitle title="General information" description="" />
        <div className={`${baseClass}__header-content`}>
          <div className={`${baseClass}__description`}>
            Your current primary email address is{' '}
            <span className={`${baseClass}__current-email`}>
              {data.email || 'yourname@example.com'}
            </span>
            {data.isEmailConfirmed === 'false' && (
              <span className={`${baseClass}__status`}> (not confirmed)</span>
            )}
          </div>
        </div>
      </UISectionHeader>

      <UISectionBody>
        <UIFormRow>
          <UIFormLabels>
            <UIFormLabel>Email</UIFormLabel>
            <UIFormLabel>First name</UIFormLabel>
            <UIFormLabel>Last name</UIFormLabel>
            <UIFormLabel>Your photo</UIFormLabel>
            <UIFormLabel>Change your photo</UIFormLabel>
          </UIFormLabels>

          <UIFormFields>
            <UIInput
              placeholder="Email"
              fullWidth
              value={data.email || ''}
              disabled
            />
            <UIInput
              placeholder="First name"
              fullWidth
              value={data.firstname || ''}
              onChange={(e) => handleChange('firstname', e.target.value)}
              disabled={saving}
            />
            <UIInput
              placeholder="Last name"
              fullWidth
              value={data.lastname || ''}
              onChange={(e) => handleChange('lastname', e.target.value)}
              disabled={saving}
            />
            <UIAvatarUpload
              currentAvatar={getCurrentAvatar()}
              placeholder="/ava/photo.png"
              onFileSelect={handleAvatarSelect}
              onRemove={handleAvatarRemove}
              onError={handleAvatarError}
              loading={saving}
              disabled={saving}
            />
            <div></div>
          </UIFormFields>
        </UIFormRow>
      </UISectionBody>

      <UISectionFooter>
        <div className={`${baseClass}__buttons`}>
          <UIButton
            variant="primary"
            onClick={handleSaveClick}
            loading={saving}
            disabled={!canSave}
          >
            Save
          </UIButton>
          <UIButton
            variant="primary"
            outline
            onClick={handleClearClick}
            disabled={saving}
          >
            Clear
          </UIButton>
        </div>
      </UISectionFooter>
    </UISectionWrapper>
  );
};

export default GeneralSettingsSection;
